/**
 * AddPlaneWizard - Main wizard modal for adding aircraft to the ramp
 *
 * A wizard that guides users through (Aircraft first):
 * 0. Aircraft type + tail number (with "Place on Map" quick path)
 * 1. Client selection/creation (skipped in quick-place mode)
 * 2. Contract type (skipped in quick-place mode)
 * 3. Location (dropdown or map placement)
 */

import { useCallback, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, Plane } from 'lucide-react'

import { WizardStepIndicatorWithLabels } from './WizardStepIndicator'
import { ClientStep } from './steps/ClientStep'
import { AircraftStep } from './steps/AircraftStep'
import { ContractStep } from './steps/ContractStep'
import { LocationStep } from './steps/LocationStep'
import { useAddPlaneWizard } from './hooks/useAddPlaneWizard'
import {
  useAddPlaneWizardStore,
  WIZARD_STEPS,
  type WizardStep,
} from '@/stores/addPlaneWizardStore'

interface AddPlaneWizardProps {
  locationId?: string
  onComplete?: () => void
  onPlacementModeStart?: () => void
}

export function AddPlaneWizard({
  locationId: locationIdProp,
  onComplete,
  onPlacementModeStart,
}: AddPlaneWizardProps) {
  const {
    isOpen,
    closeWizard,
    currentStep,
    nextStep,
    prevStep,
    goToStep,
    isStepComplete,
    canProceedToStep,
    isSubmitting: isSubmittingStore,
    isQuickPlaceMode,
    activatePlacementMode,
    reset,
    wizardContext,
    tailNumber: storeTailNumber,
    selectedAircraftTypeName,
  } = useAddPlaneWizardStore()

  // Resolve locationId: prop > wizard context > null
  const locationId = locationIdProp || wizardContext.locationId || ''

  // Use the submission hook
  const { submit, isSubmitting: isSubmittingHook } = useAddPlaneWizard({
    locationId,
    onSuccess: () => {
      onComplete?.()
    },
  })

  // Combine submitting states
  const isSubmitting = isSubmittingStore || isSubmittingHook

  const isCompleteSetup = wizardContext.source === 'complete_setup'

  // Steps to display in the indicator (dynamic based on mode)
  const visibleSteps = useMemo(() => {
    if (isCompleteSetup) {
      return ['Client', 'Contract'] as const
    }
    if (isQuickPlaceMode) {
      return ['Aircraft', 'Location'] as const
    }
    return WIZARD_STEPS
  }, [isQuickPlaceMode, isCompleteSetup])

  // Map visual step index to real step index
  const visibleToRealStep = useCallback(
    (visibleIndex: number): WizardStep => {
      if (isCompleteSetup) {
        // visible 0 → real 1 (Client), visible 1 → real 2 (Contract)
        return (visibleIndex + 1) as WizardStep
      }
      if (isQuickPlaceMode) {
        return visibleIndex === 0 ? 0 : 3
      }
      return visibleIndex as WizardStep
    },
    [isQuickPlaceMode, isCompleteSetup]
  )

  // Map real step index to visual step index
  const realToVisibleStep = useCallback(
    (realStep: WizardStep): number => {
      if (isCompleteSetup) {
        // real 1 → visible 0, real 2 → visible 1
        return realStep - 1
      }
      if (isQuickPlaceMode) {
        return realStep === 0 ? 0 : 1
      }
      return realStep
    },
    [isQuickPlaceMode, isCompleteSetup]
  )

  // Handle step navigation with click
  const handleStepClick = useCallback(
    (visibleIndex: number) => {
      const realStep = visibleToRealStep(visibleIndex)
      if (canProceedToStep(realStep)) {
        goToStep(realStep)
      }
    },
    [canProceedToStep, goToStep, visibleToRealStep]
  )

  // Handle next button
  const handleNext = useCallback(() => {
    if (isStepComplete(currentStep)) {
      if (isCompleteSetup && currentStep === 2) {
        // Complete setup: last step is Contract (step 2) — submit
        handleSubmit()
        return
      }
      if (isQuickPlaceMode && currentStep === 0) {
        // Quick-place mode: jump from Aircraft to Location
        goToStep(3)
      } else {
        nextStep()
      }
    }
  }, [currentStep, isStepComplete, nextStep, isQuickPlaceMode, isCompleteSetup, goToStep])

  // Handle back button
  const handleBack = useCallback(() => {
    if (isQuickPlaceMode && currentStep === 3) {
      // Quick-place mode: go back from Location to Aircraft
      goToStep(0)
    } else {
      prevStep()
    }
  }, [isQuickPlaceMode, currentStep, goToStep, prevStep])

  // Handle "Place on Map" from location step
  const handlePlaceOnMap = useCallback(() => {
    activatePlacementMode()
    onPlacementModeStart?.()
  }, [activatePlacementMode, onPlacementModeStart])

  // Handle quick place from aircraft step (activates quick-place mode,
  // which jumps to location step via the store's activateQuickPlace)
  const handleQuickPlace = useCallback(() => {
    // Store already sets isQuickPlaceMode and jumps to step 3
    // We don't need to do anything extra here
  }, [])

  // Handle wizard close
  const handleClose = useCallback(() => {
    closeWizard()
    // Don't reset if in placement mode (preserve data)
    if (!useAddPlaneWizardStore.getState().isPlacementModeActive) {
      reset()
    }
  }, [closeWizard, reset])

  // Handle final submission (from location step)
  const handleSubmit = useCallback(() => {
    submit()
  }, [submit])

  // Determine if we can proceed
  const canProceed = isStepComplete(currentStep)
  const isLastStep = isCompleteSetup ? currentStep === 2 : currentStep === 3

  // Step completion checker for the visual indicator
  const isVisibleStepComplete = useCallback(
    (visibleIndex: number) => {
      const realStep = visibleToRealStep(visibleIndex)
      return isStepComplete(realStep)
    },
    [isStepComplete, visibleToRealStep]
  )

  // Dynamic title based on wizard source
  const getWizardTitle = () => {
    switch (wizardContext.source) {
      case 'transient':
        return 'New Transient Booking'
      case 'lease':
        return 'New Lease Contract'
      case 'complete_setup':
        return 'Complete Aircraft Setup'
      default:
        return 'Add New Aircraft'
    }
  }

  // Only show quick-place in ramp mode
  const showQuickPlace = wizardContext.source === 'ramp'

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <AircraftStep onQuickPlace={showQuickPlace ? handleQuickPlace : undefined} />
      case 1:
        return <ClientStep />
      case 2:
        return <ContractStep />
      case 3:
        return (
          <LocationStep
            locationId={locationId || undefined}
            onPlaceOnMap={handlePlaceOnMap}
            onSubmit={handleSubmit}
          />
        )
      default:
        return null
    }
  }

  // Get step description text
  const getStepDescription = () => {
    const visibleIndex = realToVisibleStep(currentStep)
    const totalSteps = visibleSteps.length
    const stepName = visibleSteps[visibleIndex] || WIZARD_STEPS[currentStep]
    return `${stepName} - Step ${visibleIndex + 1} of ${totalSteps}`
  }

  // Handle Dialog open state changes
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      handleClose()
    }
  }, [handleClose])

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] min-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold">
            {getWizardTitle()}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {getStepDescription()}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="px-6 pt-4">
          <WizardStepIndicatorWithLabels
            steps={visibleSteps}
            currentStep={realToVisibleStep(currentStep)}
            isStepComplete={isVisibleStepComplete}
            onStepClick={handleStepClick}
            canNavigate
          />
        </div>

        {/* Aircraft info banner for complete_setup mode */}
        {isCompleteSetup && (
          <div className="mx-6 mt-2 mb-0 px-3 py-2 bg-muted/50 border border-border/50 rounded-md flex items-center gap-2">
            <Plane className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-mono font-semibold">{storeTailNumber}</span>
            {selectedAircraftTypeName && (
              <span className="text-xs text-muted-foreground">({selectedAircraftTypeName})</span>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {renderStepContent()}
        </div>

        {/* Footer with navigation (hidden for location step which has its own buttons, except in complete_setup mode) */}
        <div
          className={`flex justify-between items-center px-6 py-4 border-t bg-muted/30 ${
            currentStep === 3 && !isCompleteSetup ? 'hidden' : ''
          }`}
        >
          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            disabled={(isCompleteSetup ? currentStep === 1 : currentStep === 0) || isSubmitting}
          >
            Back
          </Button>

          <Button
            type="button"
            onClick={handleNext}
            disabled={!canProceed || isSubmitting}
          >
            {isSubmitting && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            {isLastStep
              ? isCompleteSetup
                ? 'Complete Setup'
                : wizardContext.source === 'transient'
                  ? 'Create Booking'
                  : wizardContext.source === 'lease'
                    ? 'Create Lease'
                    : 'Complete'
              : 'Continue Setup'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddPlaneWizard
